import { useEffect, useState } from 'react';
import { ActionIcon, Badge, Button, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { IconBellPlus, IconCheck, IconExternalLink, IconMessageCircleQuestion } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { CreateTodoInput, Todo } from '../lib/api';
import { TodoFormModal } from '../pages/todos/TodoFormModal';
import { CompleteTodoModal } from '../pages/todos/CompleteTodoModal';
import { SnoozeMenu } from '../pages/todos/TodoDrawer';
import type { TodoFormDefaults } from '../pages/todos/TodoFormModal';
import { RESOLVE_WHEN_SHORT, addDaysYmd, berlinYmd, dueColor, formatDue, workdaysAheadYmd } from '../pages/todos/todoLabels';
import { useTodoActions } from '../pages/todos/useTodoActions';

/**
 * Aufgaben & Erinnerungen am Ticket (Glocke im Cockpit). Zeigt die Aufgaben der Leitung an diesem
 * Ticket für alle, persönliche Erinnerungen nur ihrem Besitzer. Anlegen ist dieselbe Maske wie auf
 * der Seite /reminders — Techniker für sich, die Leitung auch für andere.
 */

/** Über Cockpit-Popover (zIndex 600) und Drawer liegen. */
const MODAL_Z = 2000;

/** Abfrage-Schlüssel, den auch die Cockpits für den Zähler an der Glocke nutzen. */
export function ticketTodosKey(provider: 'weclapp' | 'cw', id: string | number) {
  return ['todos', 'ticket', provider, String(id)] as const;
}

interface PanelProps {
  provider: 'weclapp' | 'cw';
  ticketId: string | number;
  ticketLabel: string;
  /** Betreff — Titel-Vorschlag fürs Nachfassen */
  subject?: string | null;
  links: CreateTodoInput['links'];
  /**
   * Meldet, ob ein Dialog offen ist. Das Panel steckt in einem Popover; Mantine rendert Modals
   * per Portal in den <body> — ein Klick ins Modal zählt für das Popover deshalb als „outside"
   * und würde es schließen. Die Eltern setzen darum solange closeOnClickOutside={false}.
   */
  onModalOpenChange?: (open: boolean) => void;
}

function TicketTodoPanel({ provider, ticketId, ticketLabel, subject, links, onModalOpenChange }: PanelProps) {
  const [formDefaults, setFormDefaults] = useState<TodoFormDefaults | null>(null);
  const formOpen = formDefaults !== null;
  const [completing, setCompleting] = useState<Todo | null>(null);
  const { completeNow } = useTodoActions();

  useEffect(() => {
    onModalOpenChange?.(formOpen || completing !== null);
  }, [formOpen, completing, onModalOpenChange]);

  const query = useQuery({
    queryKey: ticketTodosKey(provider, ticketId),
    queryFn: () => api.getTicketTodos(provider, ticketId),
    enabled: !!ticketId,
    staleTime: 30_000,
  });
  const todos = query.data?.todos ?? [];

  return (
    <Stack gap="xs" p="sm">
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          Aufgaben & Erinnerungen
        </Text>
        <Group gap={4}>
          {/* Nachfassen nur bei weclapp: nur dort erkennt das Portal eine Antwort des Kunden. */}
          {provider === 'weclapp' && (
            <Tooltip label="Erinnert dich — außer der Kunde antwortet vorher" withinPortal={false}>
              <Button
                size="compact-xs"
                variant="light"
                color="teal"
                leftSection={<IconMessageCircleQuestion size={12} />}
                onClick={() =>
                  setFormDefaults({
                    title: `Nachfassen: ${subject ?? ticketLabel}`.slice(0, 300),
                    links,
                    linkLabel: ticketLabel,
                    source: 'TICKET',
                    due: { date: workdaysAheadYmd(2), time: null },
                    resolveWhen: 'CUSTOMER_REPLIED',
                  })
                }
              >
                Nachfassen
              </Button>
            </Tooltip>
          )}
          <Button
            size="compact-xs"
            variant="light"
            color="grape"
            leftSection={<IconBellPlus size={12} />}
            onClick={() =>
              setFormDefaults({
                title: '',
                links,
                linkLabel: ticketLabel,
                source: 'TICKET',
                due: { date: addDaysYmd(berlinYmd(), 1), time: null },
              })
            }
          >
            Erinnern
          </Button>
        </Group>
      </Group>

      {query.isLoading ? (
        <Loader size="xs" />
      ) : todos.length === 0 ? (
        <Text size="xs" c="dimmed">
          Nichts offen zu diesem Ticket.
        </Text>
      ) : (
        todos.map((t) => (
          <Group key={t.id} justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
            <div style={{ minWidth: 0, flex: 1 }}>
              <Text size="xs" fw={500} truncate>
                {t.title}
              </Text>
              <Group gap={4} wrap="wrap">
                <Badge size="xs" variant="light" color={dueColor(t)} style={{ textTransform: 'none' }}>
                  {t.overdue ? 'überfällig · ' : ''}
                  {formatDue(t.due)}
                </Badge>
                {t.assignee && (
                  <Badge size="xs" variant="outline" color="gray" style={{ textTransform: 'none' }}>
                    {t.kind === 'ASSIGNED' ? t.assignee.displayName : 'meine'}
                  </Badge>
                )}
                {t.snoozeCount > 0 && (
                  <Badge size="xs" variant="outline" color="orange" style={{ textTransform: 'none' }}>
                    {t.snoozeCount}× verschoben
                  </Badge>
                )}
                {t.resolveWhen && (
                  <Badge size="xs" variant="outline" color="teal" style={{ textTransform: 'none' }}>
                    {RESOLVE_WHEN_SHORT[t.resolveWhen]}
                  </Badge>
                )}
              </Group>
            </div>
            <Group gap={2} wrap="nowrap">
              {t.permissions.canSnooze && <SnoozeMenu todo={t} compact inline zIndex={MODAL_Z} />}
              {t.permissions.canComplete && (
                <Tooltip label="Erledigt" withinPortal={false}>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="green"
                    loading={completeNow.isPending}
                    onClick={() => (t.kind === 'ASSIGNED' ? setCompleting(t) : completeNow.mutate(t.id))}
                    aria-label="Erledigt"
                  >
                    <IconCheck size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label="Öffnen" withinPortal={false}>
                <ActionIcon size="sm" variant="subtle" color="gray" component={Link} to={`/reminders?open=${t.id}`} aria-label="Öffnen">
                  <IconExternalLink size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        ))
      )}

      <TodoFormModal opened={formOpen} onClose={() => setFormDefaults(null)} zIndex={MODAL_Z} defaults={formDefaults ?? undefined} />
      <CompleteTodoModal todo={completing} onClose={() => setCompleting(null)} zIndex={MODAL_Z} />
    </Stack>
  );
}

interface TicketReminderPanelProps {
  cwTicketId: number;
  ticketSummary?: string | null;
  companyName?: string | null;
  onModalOpenChange?: (open: boolean) => void;
}

/** ConnectWise-Cockpit */
export function TicketReminderPanel({ cwTicketId, ticketSummary, companyName, onModalOpenChange }: TicketReminderPanelProps) {
  return (
    <TicketTodoPanel
      provider="cw"
      ticketId={cwTicketId}
      ticketLabel={`#${cwTicketId}${ticketSummary ? ` · ${ticketSummary}` : ''}`}
      subject={ticketSummary}
      links={{ cwTicket: { id: cwTicketId, summary: ticketSummary ?? null, companyName: companyName ?? null } }}
      onModalOpenChange={onModalOpenChange}
    />
  );
}

interface WeclappTicketReminderPanelProps {
  weclappTicketId: string;
  weclappTicketNumber?: string;
  ticketSummary?: string | null;
  companyName?: string | null;
  /** weclapp-Party des Tickets — Kunde fürs Folgeticket beim Abhaken */
  partyId?: string | null;
  onModalOpenChange?: (open: boolean) => void;
}

/** weclapp-Cockpit */
export function WeclappTicketReminderPanel({
  weclappTicketId,
  weclappTicketNumber,
  ticketSummary,
  companyName,
  partyId,
  onModalOpenChange,
}: WeclappTicketReminderPanelProps) {
  return (
    <TicketTodoPanel
      provider="weclapp"
      ticketId={weclappTicketId}
      ticketLabel={`${weclappTicketNumber ?? weclappTicketId}${ticketSummary ? ` · ${ticketSummary}` : ''}`}
      subject={ticketSummary}
      links={{
        weclappTicket: {
          id: weclappTicketId,
          number: weclappTicketNumber ?? null,
          subject: ticketSummary ?? null,
          partyId: partyId ?? null,
          partyName: companyName ?? null,
        },
      }}
      onModalOpenChange={onModalOpenChange}
    />
  );
}
